import { create } from "zustand";
import { CreditInformationResponse, CreditType } from "../api/types";
import { getCreditInformation } from "../api/api";
import { useSDKConfigStore } from "./sdkConfigStore";

interface CreditState {
  creditInfo: Record<CreditType, CreditInformationResponse | null>;
  isLoading: boolean;
  error: string | null;
  // Internal methods for SDK use only
  _setCreditInfo: (type: CreditType, info: CreditInformationResponse) => void;
  _setLoading: (loading: boolean) => void;
  _setError: (error: string | null) => void;
  _reset: () => void;
  // New function to refresh positions
  _refreshPositions: (creditTypes: CreditType[]) => Promise<void>;
}

export const useCreditStore = create<CreditState>((set, get) => ({
  creditInfo: {
    aave: null,
    euler: null,
    hypurrFi: null,
  },
  isLoading: false,
  error: null,

  // Internal methods for SDK use only (prefixed with _)
  _setCreditInfo: (type, info) =>
    set((state) => ({
      creditInfo: {
        ...state.creditInfo,
        [type]: info,
      },
    })),

  _setLoading: (loading) => set({ isLoading: loading }),
  _setError: (error) => set({ error }),

  _reset: () =>
    set({
      creditInfo: {
        card: null,
        aave: null,
        euler: null,
      },
      isLoading: false,
      error: null,
    }),

  // New function to refresh positions for specified credit types
  _refreshPositions: async (creditTypes: CreditType[]) => {
    const { eoaAddress, isInitialized } = useSDKConfigStore.getState();

    if (!isInitialized || !eoaAddress) {
      console.error(
        "❌ SDK: Cannot refresh positions - SDK not initialized or no EOA address"
      );
      return;
    }

    console.log("🔄 SDK: Refreshing positions for:", creditTypes);

    // Set loading state
    set({ error: null });

    try {
      // Fetch all requested credit types in parallel
      const fetchPromises = creditTypes.map((creditType) =>
        getCreditInformation(
          eoaAddress,
          creditType === "aave" ? "aave-v3" : creditType
        )
      );

      const responses = await Promise.all(fetchPromises);

      // Update store with fetched data
      responses.forEach((response, index) => {
        const creditType = creditTypes[index];
        get()._setCreditInfo(
          creditType === "aave-v3" ? "aave" : creditType,
          response
        );
      });

      console.log("✅ SDK: Successfully refreshed positions for:", creditTypes);
    } catch (error) {
      console.error("❌ SDK: Error refreshing positions:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to refresh positions";
      set({ error: errorMessage });
    }
  },
}));
